import { COL_2, COL_3, COL_FULL, NOTE_TYPES, REPAYMENT_TYPES } from './fields';
import summaryShot from '../../assets/dashboard-guide/summary.jpg';
import loanDetailsShot from '../../assets/dashboard-guide/loan-details.png';
import propertyDetailsShot from '../../assets/dashboard-guide/property-details.png';
import additionalInfoShot from '../../assets/dashboard-guide/additional-info.png';
import docsUpdatesShot from '../../assets/dashboard-guide/docs-updates.png';

// WHAT the note wizard asks, for both surfaces (Add Property and the note detail) and for both juegos of
// steps. ONE catalogue of field definitions + one list of steps per product family; the wizard
// (NoteWizard.js) only knows how to paint them. Nothing here renders — a definition is data, so the same
// box carries the same label, the same width and the same rules whether it is being created or edited.
//
// TWO step sets, because two very different things are being described (TL, 2026-09-10):
//   INDIVIDUAL_STEPS — the Individual Note: one step per section of the investor's fact sheet, in the
//     order the investor reads it, with that section's REAL screenshot beside the fields.
//   PRODUCT_STEPS   — everything the system mints (Credit Fund, Constitution Note, Rolling, Demand):
//     those products have NO per-note fact sheet on the dashboard (they are product pages, and the widget
//     reads a handful of columns off the note), so the property boxes do not apply and the screenshots
//     would be a lie. What is left is what the investor reads or the operator may touch, plus a line
//     saying who owns the rest.

const required = (label) => [
  { required: true, message: `${label} is required` },
];
const plain = (values) => values.map((value) => ({ value, label: value }));

// One definition per field: the FUNCTIONAL name of the column and the input kind. On the Individual Note
// the box does not wear this name — the investor's own word does (dashboardMap), and this one survives as
// the input's placeholder; the reduced set, which has no fact sheet to borrow a word from, wears it.
// `span` defaults to the step's column (half a row beside the screenshot, a third on the Review).
// `marker`: the number the Summary screenshot pins on the element. `editOnly`: only the detail has it
// (there is no note to hang the file on while creating). `does`: the line under the label for a box the
// dashboard does NOT paint (the operator's own files); everything the dashboard paints takes its line
// from dashboardMap. `product`: the label/placeholder — and the line — the reduced set uses instead,
// because the same column means something else on a product that is not a property.
export const FIELDS = {
  // Summary — card + header
  image: {
    label: 'Property image',
    kind: 'file',
    listType: 'picture',
    button: 'Upload display image',
    marker: 1,
  },
  loanTag: { label: 'Loan tag', kind: 'tag', tagType: 'loan', marker: 2 },
  address: {
    label: 'Address',
    kind: 'text',
    placeholder: 'Property address',
    span: COL_FULL,
    rules: required('Address'),
    marker: 3,
    product: {
      label: 'Product name',
      placeholder: 'The name investors and operators read',
      does: "The name operators read, and the name this product carries in an investor's holdings.",
    },
  },
  city: {
    label: 'City',
    kind: 'text',
    span: COL_3,
    rules: required('City'),
    marker: 3,
  },
  state: {
    label: 'State',
    kind: 'text',
    span: COL_3,
    rules: required('State'),
    marker: 3,
  },
  zipCode: {
    label: 'ZIP code',
    kind: 'text',
    span: COL_3,
    rules: required('ZIP code'),
    marker: 3,
  },
  dealOverview: {
    label: 'Deal overview',
    kind: 'textarea',
    span: COL_FULL,
    placeholder: 'Why we originated this loan',
    marker: 4,
    product: {
      label: 'Description',
      placeholder: 'The blurb the product page and the widget show',
      does: 'Who reads it depends on the product — the panel beside these boxes says where it lands.',
    },
  },
  netYield: { label: 'Net yield', kind: 'percent', span: COL_3, marker: 5 },
  maturityDate: {
    label: 'Maturity date',
    kind: 'date',
    span: COL_3,
    marker: 7,
  },
  // TOTAL LOAN AMOUNT on the fact sheet, and the cap the funding rails assign every purchase against.
  // Measured on the prod dump: in 11 of the 12 notes where the two amounts differ this one is the SMALLER
  // (763 Utica, $84,902 offered against a $475,000 loan) — which is why the box says underneath that this
  // is the real cap. The functional name below is the placeholder, and the reduced set's label.
  fundingAmount: {
    label: 'Amount offered to investors (cap)',
    kind: 'currency',
    noDecimal: true,
    span: COL_3,
    rules: required('Amount offered to investors'),
    marker: 8,
  },
  // Loan Details
  investorYieldMaturity: {
    label: 'Investor yield to maturity',
    kind: 'percent',
  },
  termLength: { label: 'Term length', kind: 'months' },
  // INITIAL LOAN AMOUNT on the fact sheet: the loan itself, usually BIGGER than the cap above — the note
  // sells a slice of this loan, not the whole of it — and read by nothing but the screen. Off the fact
  // sheet it is the one number Alto is told, so the reduced set names it after that.
  investmentAmount: {
    label: 'Loan size (initial advance)',
    kind: 'currency',
    noDecimal: true,
    rules: required('Loan size'),
    product: {
      label: 'Offering size (Alto valuation cap)',
      does: 'What Alto is told the offering is worth — its valuation cap. It is not the cap investors fill.',
    },
  },
  rehabBudget: { label: 'Rehab budget', kind: 'currency' },
  arv: { label: 'ARV', kind: 'currency' },
  asIsValuePurchasePrice: {
    label: 'As-Is value or purchase price',
    kind: 'currency',
  },
  asIsLtv: { label: 'As-Is LTV', kind: 'percent' },
  arvLtv: { label: 'ARV LTV', kind: 'percent' },
  // Property Details
  propertyTag: { label: 'Property tag', kind: 'tag', tagType: 'property' },
  numberOfUnits: { label: 'Number of units', kind: 'text' },
  // Additional Info
  loanPurpose: { label: 'Loan purpose', kind: 'text' },
  originalLoanTerm: { label: 'Original loan term', kind: 'months' },
  notePosition: { label: 'Note position', kind: 'text' },
  repaymentType: {
    label: 'Repayment type',
    kind: 'select',
    options: plain(REPAYMENT_TYPES),
  },
  borrowerCreditScore: { label: 'Borrower credit score', kind: 'text' },
  borrowerTrackRecord: { label: 'Borrower track record', kind: 'text' },
  // Docs & Updates
  documents: {
    label: 'Documents',
    kind: 'file',
    multiple: true,
    button: 'Upload document files',
    span: COL_2,
    product: {
      does: 'Never on the storefront — the product page carries its own document list.',
    },
  },
  updates: {
    label: 'Updates',
    kind: 'updates',
    editOnly: true,
    span: COL_2,
    does: 'News about the deal, shown to investors under the note. Written here on an Individual or a Rolling Note; read-only on the products the system mints.',
  },
};

// Where the Summary screenshot pins each number (percent of the image): 1 hero image, 2 tag line,
// 3 title, 4 "Why we originated this loan", 5–8 the stat cards.
const SUMMARY_MARKERS = [
  { n: 1, left: 2.5, top: 4.5 },
  { n: 2, left: 26.5, top: 38 },
  { n: 3, left: 79, top: 44.5 },
  { n: 4, left: 39.5, top: 54.5 },
  { n: 5, left: 21.5, top: 81 },
  { n: 6, left: 46, top: 81 },
  { n: 7, left: 70.5, top: 81 },
  { n: 8, left: 95, top: 81 },
];

const REVIEW_STEP = {
  key: 'review',
  title: 'Review',
  caption: 'Check & confirm',
};

// The Individual Note, in the investor's reading order. `dashboard` names the fact-sheet section the step
// mirrors and `guide` its real screenshot. Each group is one Card.
export const INDIVIDUAL_STEPS = [
  {
    key: 'summary',
    title: 'Summary',
    caption: 'Card & header',
    dashboard: 'summary',
    guide: {
      src: summaryShot,
      markers: SUMMARY_MARKERS,
      note: '2 without tags: Repayment type | Loan purpose · 6 is As-Is LTV (Loan Details step)',
    },
    groups: [
      {
        fields: [
          'image',
          'loanTag',
          'address',
          'city',
          'state',
          'zipCode',
          'dealOverview',
          'netYield',
          'maturityDate',
          'fundingAmount',
        ],
      },
    ],
  },
  {
    key: 'loan',
    title: 'Loan Details',
    dashboard: 'loan',
    guide: { src: loanDetailsShot },
    groups: [
      {
        fields: [
          'investorYieldMaturity',
          'termLength',
          'investmentAmount',
          'rehabBudget',
          'arv',
          'asIsValuePurchasePrice',
          'asIsLtv',
          'arvLtv',
        ],
      },
    ],
  },
  {
    key: 'property',
    title: 'Property Details',
    dashboard: 'property',
    guide: { src: propertyDetailsShot },
    groups: [{ fields: ['propertyTag', 'numberOfUnits'] }],
  },
  {
    key: 'additional',
    title: 'Additional Info',
    dashboard: 'additional',
    guide: { src: additionalInfoShot },
    groups: [
      {
        title: 'Loan',
        fields: [
          'loanPurpose',
          'originalLoanTerm',
          'notePosition',
          'repaymentType',
        ],
      },
      {
        title: 'Borrower info',
        fields: ['borrowerCreditScore', 'borrowerTrackRecord'],
      },
    ],
  },
  {
    key: 'docs',
    title: 'Docs & Updates',
    dashboard: 'docs',
    guide: { src: docsUpdatesShot },
    groups: [{ fields: ['documents', 'updates'] }],
  },
  REVIEW_STEP,
];

// The reduced set. No screenshots: the products the system mints have no per-note fact sheet to shoot
// (`Public/ConstitutionNotes` and `Public/CreditFund` are PRODUCT pages, and the widget reads a handful
// of columns off the note). `aside` is what the screenshot panel becomes: the lines that say who owns
// what this form does not ask.
export const PRODUCT_STEPS = [
  {
    key: 'product',
    title: 'Product',
    caption: 'What investors read',
    aside: 'product',
    groups: [
      {
        fields: [
          'address',
          'dealOverview',
          'netYield',
          'fundingAmount',
          'investmentAmount',
          'maturityDate',
          'termLength',
        ],
      },
    ],
  },
  // Paperwork is a PROPERTY's: measured 2026-09-18 in the prod dump (14 files, all on property-backed
  // notes) and in the backend (the two PDFs Alto asks for are static and sent by the system, never the
  // note's uploads). The products the system mints get no Documents box; Updates stay only where they
  // are written here (Rolling) — the rules below hide the rest, and an emptied step is dropped.
  {
    key: 'updates',
    title: 'Updates',
    caption: 'News for investors',
    aside: 'updates',
    groups: [{ fields: ['documents', 'updates'] }],
  },
  REVIEW_STEP,
];

// The columns of the reduced set a product does NOT carry, per note type: the box is dropped altogether
// and the reason is printed in the step's aside. Everything not named here is the operator's to type
// (subject to the ledger locks the wizard paints) or is shown disabled with who owns it.
//
// MEASURED on investor-dashboard-next, 2026-09-10 — this is not a guess:
//   · `/constitution-notes` and `/credit-fund` are PRODUCT pages, not per-note fact sheets (no `:id`
//     route, and a grep for note columns over both trees returns nothing).
//   · The Constitution/Rolling widget maps a note down to FIVE columns (`features/investment/common/
//     note.mappers.ts`): `_id`, `noteType`, `termPeriod`, `maturityDate`, `netYield`. Rolling replaces
//     the last two with the literals 'Rolling' / '21 day rolling'.
//   · The Credit Fund flow reads ONE column, `_id`, and paints nothing with it — NAV, 12-month return
//     and the $100k example are CMS content (`landingCreditFund`).
//   · Off the storefront, `fundingAmount` is the cap every rail allocates against (transaction-item/
//     modules/investment § readTarget) and `investmentAmount` is the valuation cap Alto is told
//     (investment/modules/alto-offering § offering.mappers) — neither is on a screen, both are the
//     operator's, so both stay.
export const PRODUCT_RULES = {
  [NOTE_TYPES.constitution]: {
    // Term and maturity belong to the cycle: the backend DROPS them from a save
    // (investment/modules/admin/mapping § CONSTITUTION_CYCLE_FIELDS), so asking for them would lie.
    // No paperwork (a property's) and updates are the system's here.
    hidden: ['maturityDate', 'termLength', 'documents', 'updates'],
    lines: [
      "Net yield, maturity date, term period, product name, listing and availability are the CYCLE's: the note-cycle scheduler mints one note per term from the configuration in Retool. The backend drops them from a save here.",
      "Investors read term, maturity and yield off this note — in the widget's note picker, not in a fact sheet. /constitution-notes is a product page with its own copy.",
      'The cycle itself — term period, start date, previous and next cycle — is in System (read-only) below.',
      "The amount offered to investors and the Alto offering size are NOT the cycle's — they are yours, until the first ledger entry freezes them (a box marked `system` is one this note already closed).",
      'The description is not on any investor screen for this product: /constitution-notes carries its own copy. It is what the next operator reads.',
    ],
  },
  [NOTE_TYPES.rolling]: {
    // Perpetual: the widget prints 'Rolling' and '21 day rolling' as literals, so neither column is read.
    // No paperwork (a property's); updates ARE written here, so that step stays.
    hidden: ['maturityDate', 'termLength', 'documents'],
    lines: [
      'Perpetual product — no maturity date and no term length: the widget prints "Rolling" and "21 day rolling" as fixed text, whatever the note stores.',
      'The net yield IS the product: every change is stamped into the yield history (System, read-only below) and stays editable even after money has moved — the only product where that is true.',
      "It has no page of its own: it is sold from the Constitution Notes product page, under the picker's divider.",
      'Product type and note listing were fixed at creation and can never be re-pointed; the offered cap and the offering size freeze with the first ledger entry (a box marked `system` is one this note already closed).',
      'The description is not on any investor screen for this product — it is what the next operator reads.',
    ],
  },
  [NOTE_TYPES.creditFund]: {
    // A perpetual offering declares no capacity BY DESIGN (funding.service § readTarget) and carries no
    // rate: the NAV and the returns investors read are CMS content, not columns.
    hidden: [
      'maturityDate',
      'termLength',
      'netYield',
      'fundingAmount',
      'documents',
      'updates',
    ],
    lines: [
      'Perpetual offering — no maturity, no term and nothing offered as a cap: it has no slice to run out of, so the rails deliberately read no capacity off it.',
      'No rate on the note either: NAV, the 12-month return and the $100k example investors read come from the CMS (landingCreditFund), not from here.',
      "The description IS read: it is the fund blurb on an investor's holding page (canned copy shows when it is empty).",
      'A single fund, not one note per deal — the backend refuses a second one. Its type and listing were fixed at creation.',
    ],
  },
  [NOTE_TYPES.demand]: {
    hidden: ['maturityDate', 'termLength', 'documents', 'updates'],
    lines: [
      'One note the platform keeps open — no maturity date and no term length.',
      'Product type and note listing were fixed when the note was created and can never be re-pointed.',
    ],
  },
};

const DEFAULT_PRODUCT_RULES = { hidden: [], lines: [] };

const UPDATES_LINES = [
  'News about the deal, shown to investors under the note on their holdings page.',
  'No paperwork here: documents belong to a property-backed note, and the two PDFs Alto asks for on an offering are fixed files the system sends on its own.',
];

export function productRules(noteType) {
  return PRODUCT_RULES[noteType] || DEFAULT_PRODUCT_RULES;
}

// The aside of a step of the reduced set: the "where does the rest come from" lines the TL asked for.
export function asideLines(step, noteType) {
  if (step.aside === 'updates') return UPDATES_LINES;
  return productRules(noteType).lines;
}

export const isIndividual = (noteType) =>
  !noteType || noteType === NOTE_TYPES.individual;

// The step set a note gets. A create is always an Individual Note: the backend refuses to type any other
// product by hand (INVESTMENT_TYPE_NOT_CREATABLE).
export function stepsFor(noteType, mode) {
  if (mode === 'create' || isIndividual(noteType)) return INDIVIDUAL_STEPS;
  const { hidden } = productRules(noteType);
  if (hidden.length === 0) return PRODUCT_STEPS;
  return PRODUCT_STEPS.map(
    (step) =>
      step.groups
        ? {
            ...step,
            groups: step.groups.map((group) => ({
              ...group,
              fields: group.fields.filter((name) => !hidden.includes(name)),
            })),
          }
        : step
    // A step with nothing left to ask is not shown at all (Credit Fund and Constitution Notes lose
    // the Updates step entirely).
  ).filter(
    (step) =>
      !step.groups || step.groups.some((group) => group.fields.length > 0)
  );
}

export const stepFields = (step) =>
  (step.groups || []).reduce((names, group) => names.concat(group.fields), []);

// The note columns the detail seeds into the form — and therefore the only ones a Save carries back
// (besides the files, the tags and the date, which the page shapes for their inputs). Derived from the
// step set in play, so a box the reduced set does not show is never seeded and never sent: an update
// without a column leaves it exactly as stored.
const NOT_A_COLUMN = [
  'image',
  'loanTag',
  'propertyTag',
  'maturityDate',
  'documents',
  'updates',
];

export function noteColumns(steps) {
  return steps
    .reduce((names, step) => names.concat(stepFields(step)), [])
    .filter((name) => !NOT_A_COLUMN.includes(name));
}
